import {FC} from 'preact/compat'
import {MasterDataTable} from '../../../components/DataTable/MasterDataTable'
import {useEffect, useState} from 'react'
import {Label} from '../../../interfaces/Label'
import {TableColumn} from 'react-data-table-component'
import {AddLabel} from '../../../components/Labels/AddLabel'
import useUser from '../../../hooks/User'
import {IconButton, Menu, MenuItem} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {DeleteLabel} from '../../../components/Labels/DeleteLabel'
import {Modal} from 'bootstrap'
import {UpdateLabel} from '../../../components/Labels/UpdateLabel'
import {useIntl} from 'react-intl'
import {CellContext, ColumnDef} from '@tanstack/react-table'
import {MasterDataTableTanstack} from '../../../components/DataTable/MasterDataTableTanstack'
import useDynamicTab from '../../../hooks/useDynamicTab'
import {settings} from 'cluster'
import {useSettingsStore} from '../../../stores/SettingsStore'
import useModalEffect from '../../../hooks/useModalEffect'

const MasterLabels: FC = () => {
  const user = useUser()
  const {settings} = useSettingsStore()
  const [label, setLabel] = useState<Label>()
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const intl = useIntl()

  const handleClick = (menuId: string) => {
    setOpenMenuId(menuId)
  }
  const handleClose = () => {
    setOpenMenuId(null)
  }

  const columns: ColumnDef<Label>[] = [
    {
      id: 'action',
      meta: {
        // size: 12,
        // maxSize: 12,
        styleHeader: {
          alignContent: 'center',
          textAlign: 'center',
        },
        styleBody: {
          alignContent: 'center',
          textAlign: 'center',
          width: '30px',
          overflow: 'auto',
        },
        isPinnedLeft: true,
      },
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.ACTION'}),
      cell: (props: CellContext<Label, unknown>) => {
        const row = props.row.original
        return (
          <>
            {(row.creatorId === user.data.id || user.data.is_superadmin) && (
              <>
                <IconButton
                  aria-label={`more-${row.id}`}
                  id={`menu-anchor-${row.id}`}
                  onClick={() => handleClick(row.id)}
                >
                  <MoreVertIcon />
                </IconButton>

                <Menu
                  id={`menu-${row.id}`}
                  anchorEl={document.getElementById(`menu-anchor-${row.id}`)}
                  open={openMenuId === row.id}
                  onClose={handleClose}
                  disableScrollLock={true}
                >
                  {(row.creatorId === user.data.id || user.data.is_superadmin) && (
                    <MenuItem
                      onClick={() => {
                        handleClose()
                        setShowUpdateModal(true)
                        setLabel(row)
                      }}
                    >
                      <i className='fas fa-edit fs-4 me-4'></i>
                      <span>{intl.formatMessage({id: 'FORM.ACTION.UPDATE'})}</span>
                    </MenuItem>
                  )}
                  {(row.creatorId === user.data.id || user.data.is_superadmin) && (
                    <MenuItem
                      onClick={() => {
                        handleClose()
                        setShowDeleteModal(true)
                        setLabel(row)
                      }}
                    >
                      <i className='fas fa-trash fs-3 me-5'></i>
                      <span>{intl.formatMessage({id: 'FORM.ACTION.DELETE'})}</span>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </>
        )
      },
    },
    {
      meta: {
        // size: 12,
        // maxSize: 12,
        styleHeader: {
          alignContent: 'center',
          textAlign: 'left',
        },
        styleBody: {
          alignContent: 'center',
          textAlign: 'left',
          overflow: 'auto',
          // width: '100%',
        },
        isPinnedLeft: false,
      },
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NAME'}),
      accessorKey: 'name',
    },
    {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.TYPE'}),
      // wrap: true,
      // allowOverflow: true,
      // style: {
      //     display: "flex",
      //     flexWrap: "wrap"
      // },
      cell: (props: CellContext<Label, unknown>) => {
        const row = props.row.original
        const arrType = []
        if (row.isUsableByMeeting) {
          arrType.push(settings.meeting_title)
        }
        if (row.isUsableByMerchant) {
          arrType.push(settings.merchant_title)
        }
        if (row.isUsableByPipeline) {
          arrType.push(settings.pipeline_title)
        }
        if (row.isUsableByProduct) {
          arrType.push(intl.formatMessage({id: 'FORM.OPTION.TYPE.PRODUCT'}))
        }
        return arrType.map((type) => {
          return <div className='badge badge-primary me-1 my-1'>{type}</div>
        })
      },
      accessorFn: (row: Label): string => {
        const arrType = []
        if (row.isUsableByMeeting) {
          arrType.push(settings.meeting_title)
        }
        if (row.isUsableByMerchant) {
          arrType.push(settings.merchant_title)
        }
        if (row.isUsableByPipeline) {
          arrType.push(settings.pipeline_title)
        }
        if (row.isUsableByProduct) {
          arrType.push(intl.formatMessage({id: 'FORM.OPTION.TYPE.PRODUCT'}))
        }
        return arrType.join(', ')
      },
    },
    {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.COLOR'}),
      // selector: row => row.color,
      cell: (props: CellContext<Label, unknown>) => {
        const row = props.row.original
        return (
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              backgroundColor: row.color,
            }}
          />
        )
      },
    },
  ]

  const actions = [<AddLabel />]

  useModalEffect({
    showModal: showUpdateModal,
    setShowModal: setShowUpdateModal,
    modalId: `update-label-modal-${label?.id}`
  })

  useModalEffect({
    showModal: showDeleteModal,
    setShowModal: setShowDeleteModal,
    modalId: `delete-label-modal-${label?.id}`
  })

  useDynamicTab({title: `${intl.formatMessage({id: 'MENU.LABELS'})} | CRM WooBlazz`})

  return (
    <>
      <ol className='breadcrumb text-muted fs-6 fw-bold mb-5'>
        <li className='breadcrumb-item text-dark'>{intl.formatMessage({id: 'MENU.LABELS'})}</li>
      </ol>
      {/* <MasterDataTable
                tableKey='labels'
                tableColumns={tableColumns}
                actions={actions}
                apiURL='labels'
            /> */}
      <MasterDataTableTanstack
        tableKey='labels'
        tableColumns={columns}
        actions={actions}
        apiURL='labels'
        // dateStart={`${date.getFullYear()}-${date.getMonth() + 1}-01`}
        // dateEnd={moment(date).format("YYYY-MM-DD")}
        autoReload={true}
      />
      {showUpdateModal && <UpdateLabel label={label!} />}
      {showDeleteModal && <DeleteLabel label={label!} />}
    </>
  )
}

export {MasterLabels}
